import { Button } from '@gravity-ui/uikit';
import { useLazyDownloadPerformanceSatisfactionChangesReportQuery } from 'store/api';
import styles from './styles.module.scss';
import { usePerformanceSatisfactionChangesDataQuery } from 'store/api/private/charts-api/chart-api-data';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { Box } from '@mui/material';
import { Localization_RU } from './localization';

interface ClientChange {
	establishment: string;
	clientId: number;
	changeExecution: number;
	changeSatisfaction: number;
	specialist?: string;
}

export const PerformanceSatisfactionChangesReport = ({ role }: { role: string | undefined }) => {
	const [trigger, { isFetching }] = useLazyDownloadPerformanceSatisfactionChangesReportQuery();

	const {
		data: performanceSatisfactionChangeReport,
		isLoading,
		error,
	} = usePerformanceSatisfactionChangesDataQuery(null, {
		refetchOnMountOrArgChange: true,
		refetchOnFocus: true,
		refetchOnReconnect: true,
	});

	const columns: MRT_ColumnDef<ClientChange>[] = [
		{
			header: role === 'SPECIALIST' ? 'ФИО пациента' : 'ID клиента',
			accessorKey: role === 'SPECIALIST' ? 'client' : 'clientId',
			size: 80,
			enableColumnFilter: true, // Всегда доступен фильтр для ID клиента
		},
		{
			header: 'Учреждение',
			accessorKey: 'establishment',
			enableColumnFilter: role === 'ADMIN' || role === 'MANAGER', // Фильтр только для ADMIN и MANAGER
		},
		{
			header: 'Общие изменения в выполнении (баллы)',
			accessorKey: 'changeExecution',
			enableColumnFilter: false,
			Cell: ({ cell }) => {
				const value = cell.getValue<number>();
				if (value == null) return '';
				const formattedValue = value.toFixed(1);
				return value > 0 ? `+${formattedValue}` : formattedValue;
			},
			muiTableBodyCellProps: ({ cell }) => ({
				sx: { color: cell.getValue<number>() >= 0 ? 'green' : 'crimson' },
			}),
		},
		{
			header: 'Общие изменения в удовлетворенности (баллы)',
			accessorKey: 'changeSatisfaction',
			enableColumnFilter: false,
			Cell: ({ cell }) => {
				const value = cell.getValue<number>();
				if (value == null) return '';
				const formattedValue = value.toFixed(1);
				return value > 0 ? `+${formattedValue}` : formattedValue;
			},
			muiTableBodyCellProps: ({ cell }) => ({
				sx: { color: cell.getValue<number>() >= 0 ? 'green' : 'crimson' },
			}),
		},
		...(role !== 'SPECIALIST'
			? [
					{
						header: 'Специалисты',
						accessorKey: 'specialist',
						enableColumnFilter: true,
					},
				]
			: []),
	];

	const handleDownload = async () => {
		try {
			const result = await trigger().unwrap();
			const url = window.URL.createObjectURL(result);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'Отчёт по общим изменениям в выполнении и удовлетворенности.xlsx');
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Ошибка при скачивании файла:', error);
		}
	};
	return (
		<div className={styles.chart}>
			<h2>Данные по общим изменениям в выполнении и удовлетворенности</h2>
			<Box sx={{ width: '100%', mx: 'auto', mt: 3 }}>
				<MaterialReactTable
					columns={columns}
					data={performanceSatisfactionChangeReport || []}
					muiTableHeadCellProps={{ sx: { fontWeight: 'bold' } }}
					enableColumnFilters={true}
					enablePagination={true}
					enableSorting
					localization={Localization_RU}
					enableColumnActions={false}
					enableGlobalFilter={false}
					enableDensityToggle={false}
					enableFullScreenToggle={false}
					enableHiding={false}
					enableRowActions={false}
					enableStickyHeader={false}
				/>
			</Box>
			<Button onClick={handleDownload} loading={isFetching} size="xl">
				{isFetching ? 'Скачивание...' : 'Скачать отчёт по общим изменениям в выполнении и удовлетворенности'}
			</Button>
		</div>
	);
};
